/**
 * FE-A3 — optional Activate QR camera scan (BarcodeDetector + getUserMedia).
 * Paste path remains the default; camera is HTTPS-only and capability-gated.
 */

/**
 * @returns {boolean}
 */
export function activateQrScanSupported() {
  try {
    if (typeof window === "undefined") return false;
    if (window.isSecureContext !== true) return false;
    const md = navigator && navigator.mediaDevices;
    if (!md || typeof md.getUserMedia !== "function") return false;
    return typeof window.BarcodeDetector === "function";
  } catch (_) {
    return false;
  }
}

/**
 * Honest one-liner when camera scan cannot start.
 * @returns {string}
 */
export function activateQrScanUnavailableReason() {
  try {
    if (typeof window === "undefined") return "Camera QR unavailable in this environment. Paste SN still works.";
    if (window.isSecureContext !== true) {
      return "Camera QR needs HTTPS (or localhost). Paste SN / CROC|… still works.";
    }
    const md = navigator && navigator.mediaDevices;
    if (!md || typeof md.getUserMedia !== "function") {
      return "This browser cannot open the camera. Paste SN / CROC|… still works.";
    }
    if (typeof window.BarcodeDetector !== "function") {
      return "QR camera needs Chrome/Edge with BarcodeDetector. Paste SN / CROC|… still works.";
    }
  } catch (_) {}
  return "Camera QR unavailable. Paste SN / CROC|… still works.";
}

/**
 * @param {{
 *   videoEl: HTMLVideoElement,
 *   onCode: (text: string) => void,
 *   onError?: (message: string) => void,
 * }} opts
 */
export function createActivateQrScanner(opts) {
  const videoEl = opts && opts.videoEl;
  const onCode = opts && opts.onCode;
  const onError = opts && opts.onError;
  let stream = null;
  let detector = null;
  let raf = 0;
  let stopped = true;
  let lastEmit = "";
  let lastEmitAt = 0;

  const stopTracks = () => {
    if (stream) {
      try {
        stream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch (_) {}
        });
      } catch (_) {}
      stream = null;
    }
    if (videoEl) {
      try {
        videoEl.srcObject = null;
      } catch (_) {}
    }
  };

  const tick = async () => {
    if (stopped || !detector || !videoEl) return;
    try {
      if (videoEl.readyState >= 2) {
        const codes = await detector.detect(videoEl);
        if (Array.isArray(codes) && codes.length) {
          const raw = String((codes[0] && (codes[0].rawValue || codes[0].raw_value)) || "").trim();
          if (raw) {
            const now = Date.now();
            if (raw !== lastEmit || now - lastEmitAt > 1500) {
              lastEmit = raw;
              lastEmitAt = now;
              if (typeof onCode === "function") onCode(raw);
            }
          }
        }
      }
    } catch (_) {
      /* frame skipped */
    }
    if (!stopped) raf = window.requestAnimationFrame(() => void tick());
  };

  const start = async () => {
    if (!activateQrScanSupported()) {
      const msg = activateQrScanUnavailableReason();
      if (typeof onError === "function") onError(msg);
      throw new Error(msg);
    }
    stop();
    stopped = false;
    try {
      detector = new window.BarcodeDetector({ formats: ["qr_code"] });
    } catch (e) {
      stopped = true;
      const msg = String((e && e.message) || e || activateQrScanUnavailableReason());
      if (typeof onError === "function") onError(msg);
      throw e;
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
    } catch (e) {
      stopped = true;
      detector = null;
      const msg =
        e && (e.name === "NotAllowedError" || e.name === "PermissionDeniedError")
          ? "Camera permission denied. Paste SN / CROC|… still works."
          : String((e && e.message) || e || "Camera open failed");
      if (typeof onError === "function") onError(msg);
      throw e;
    }
    if (!videoEl) {
      stopTracks();
      stopped = true;
      throw new Error("Missing video element");
    }
    videoEl.srcObject = stream;
    videoEl.setAttribute("playsinline", "true");
    videoEl.muted = true;
    try {
      await videoEl.play();
    } catch (_) {}
    raf = window.requestAnimationFrame(() => void tick());
  };

  const stop = () => {
    stopped = true;
    if (raf) {
      try {
        window.cancelAnimationFrame(raf);
      } catch (_) {}
      raf = 0;
    }
    detector = null;
    stopTracks();
  };

  return { start, stop, supported: activateQrScanSupported };
}
